import React, { useState, useRef, useEffect, ReactNode } from 'react';

interface SelectButtonProps {
  options: string[];
  placeholder?: string;
  onSelect?: (option: string) => void;
  icon?: ReactNode;
  value?: string; // Add value prop to make the component fully controlled
}

const SelectButton: React.FC<SelectButtonProps> = ({
  options,
  placeholder = "Select an option",
  icon,
  onSelect,
  value = "", // Default to empty string
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Use value prop if provided, otherwise maintain internal state
  const [selectedOption, setSelectedOption] = useState<string | null>(value || null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  
  // Update component state when value prop changes
  useEffect(() => {
    if (value !== undefined && value !== selectedOption) {
      setSelectedOption(value || null);
    }
  }, [value]);

  // Update ARIA attributes when state changes
  useEffect(() => {
    // Update button's aria-expanded attribute
    if (buttonRef.current) {
      buttonRef.current.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    
    // Update option aria-selected attributes
    optionRefs.current.forEach((el, index) => {
      if (el) {
        const option = options[index];
        el.setAttribute('aria-selected', (value || selectedOption) === option ? 'true' : 'false');
      }
    });
  }, [isOpen, selectedOption, options, value]);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectOption = (option: string) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(null);
    if (onSelect) {
      onSelect("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    function handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        className="min-w-[120px] w-full flex justify-between items-center bg-white rounded-sm text-sm py-1.5 px-3 text-slate-800 border border-slate-300 cursor-pointer"
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded="false"
        aria-label={placeholder}
      >
        <div className='flex items-center gap-1'>
          {icon && <span>{icon}</span>}
          <span>
            {selectedOption ? (
              selectedOption.includes(' (') ? (
                <>
                  {selectedOption.split(' (')[0]}
                  <span className="text-gray-400"> ({selectedOption.split(' (')[1]}</span>
                </>
              ) : (
                selectedOption
              )
            ) : (
              placeholder
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {selectedOption && (
            <span
              className="text-slate-500 hover:text-slate-700 font-bold"
              onClick={clearSelection}
              aria-label="Clear selection"
              role="button"
              tabIndex={0}
            >
              &times;
            </span>
          )}
          <svg viewBox="0 0 24 24" height="20px" width="20px" fill="currentColor" className="text-slate-500"><path d="M17,9.17a1,1,0,0,0-1.41,0L12,12.71,8.46,9.17a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.42l4.24,4.24a1,1,0,0,0,1.42,0L17,10.59A1,1,0,0,0,17,9.17Z"></path></svg>
        </div>
      </button>
      {isOpen && (
        <div
          className="absolute z-10 min-w-[150px] w-full mt-1 bg-white border border-slate-300 rounded-sm shadow-lg max-h-48 overflow-y-auto"
          role="listbox"
          tabIndex={-1}
          aria-label={`${placeholder} options`}
        >
          {options.map((option, index) => (
            <div
              key={index}
              ref={(el: HTMLDivElement | null) => { optionRefs.current.set(index, el); }}
              className="text-sm text-slate-800 hover:bg-slate-100 px-3 py-2 cursor-pointer"
              role="option"
              aria-selected="false"
              tabIndex={0}
              onClick={() => selectOption(option)}
            >
              {option.includes(' (') ? (
                <>
                  {option.split(' (')[0]}
                  <span className="text-gray-400"> ({option.split(' (')[1]}</span>
                </>
              ) : (
                option
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectButton;
